import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@primereact/ui/button';
import { InputText } from '@primereact/ui/inputtext';

import '../../common/EsCommon.css';
import './EsCampaigns.css';
import {
    API_CAMPAIGN,
    API_CAMPAIGN_JOIN,
    API_CAMPAIGN_LIST,
    VIEW_CAMPAIGN,
    VIEW_DEMO,
} from '../../common/EsConstants';
import { useEsDemoModeFlag } from '../../common/EsFlags';
import { esFetch, esPost } from '../../common/EsFetch';
import { EsCard, EsTextarea } from '../../common/EsPrime';
import { esDayMonYear } from '../../common/EsDate';
import EsRulesysSelect from '../../common/EsRulesysSelect';
import { esFocusBlankOnMount } from '../../common/EsFocusBlank';
import { esRefusalOf, useEsMaker } from '../../common/EsSubs';
import { EsLoading } from '../../common/EsLoading';
import { EsEmptyState } from '../../common/EsEmptyState';
import { useEsStrings } from '../../common/EsStrings';
import EsMinis from '../../common/EsMinis';

/**
 * A player's own campaigns, and the way to start another.
 *
 * <p>The front door of the Dashboard, and the sibling of EsCharacters in every
 * way that matters: pick one, or make one. It is what /view/campaigns renders
 * and, until phase 4 puts the moment there, what the flag-on branch of
 * /view/console renders too -- one list, two routes.
 *
 * <p><b>The list is the caller's, or for an admin everybody's after their
 * own.</b> campaignList takes no owner parameter -- which campaigns these are is
 * the session's to decide, read from the caller's membership rows and the
 * caller's role -- so there is nothing to pass here and nothing this page could
 * be tricked into asking for. See EsCampaignController.
 *
 * <p>The empty state is the first thing anybody sees, since nobody arrives here
 * with campaigns, so it is a real invitation rather than "no rows". It names
 * BOTH ways in -- create one, or join one with a code -- and since phase 3 both
 * of them are forms. The code half used to be a sentence, because
 * campaignJoin did not exist; it does now.
 *
 * <p><b>Every join refusal reads "No such campaign".</b> That is not this page
 * being unhelpful, it is the server's design: a wrong code, a blank code, a
 * campaign closed to new members and a deleted campaign are deliberately one
 * answer, so somebody trying codes cannot tell which of the four they hit. The
 * message is shown exactly as it arrives and nothing here embroiders it.
 *
 * <p>esFetch does NOT check response.ok, which is the trap that makes the
 * explicit throw below load-bearing rather than tidy: with the flag off
 * EsFlagFilter answers 404 with {"msg":"Feature not available"}, and that parses
 * perfectly well into campaignList state as if it were a row. esPost, used by
 * the create form, already checks and throws.
 */

const STATUS_LOADING = 'loading';
const STATUS_OK = 'ok';
const STATUS_ERROR = 'error';

/** A role as a word: GM stays GM, PLAYER becomes Player. */
function roleOf( t, pRole ) {
    if ( !pRole ) return null;
    if ( pRole === 'GM' ) return t( 'campaign-list-role-gm', 'GM' );
    if ( pRole === 'PLAYER' ) return t( 'campaign-list-role-player', 'Player' );
    if ( pRole === 'OBSERVER' ) return t( 'campaign-list-role-observer', 'Observer' );
    return pRole.charAt( 0 ) + pRole.slice( 1 ).toLowerCase();
}

/**
 * The card's outline by the reader's role at that table. Colour only: the meta
 * line still says GM, Player or Observer, because a colour alone is not a word.
 */
const ROLE_CARD_CLASS = {
    GM: 'campaignCardGm',
    PLAYER: 'campaignCardPlayer',
    OBSERVER: 'campaignCardObserver',
};

/**
 * A line of what this campaign is, skipping the parts the server left null.
 * ownerName is set only on a campaign an admin sees without a seat in it.
 */
function summarise( t, pCampaign ) {
    return [
        pCampaign.rulesys,
        roleOf( t, pCampaign.role ),
        pCampaign.memberCount
            ? t( 'campaign-list-member-count', '{count, plural, one {# member} other {# members}}',
                { count: pCampaign.memberCount } )
            : null,
        pCampaign.ownerName && t( 'campaign-list-owned-by', 'owned by {name}', { name: pCampaign.ownerName } ),
    ].filter( Boolean ).join( ' · ' );
}

/** The last time anybody played, as a date, or '' when they never have. */
function lastPlayed( t, pCampaign ) {
    if ( !pCampaign.lastPlayed ) return '';
    return t( 'campaign-list-last-played', 'Last played {date}', { date: esDayMonYear( pCampaign.lastPlayed ) } );
}

// rulesysId null until the Rulesys dropdown's list arrives and hands back the
// starting row (D&D 5.5), which is what the box shows.
const BLANK_FORM = { name: '', blurb: '', rulesysId: null };

export default function EsCampaigns() {
    const t = useEsStrings();
    const maker = useEsMaker();
    const demoOn = useEsDemoModeFlag();
    const [ status, setStatus ] = useState( STATUS_LOADING );
    const [ campaignList, setCampaignList ] = useState( [] );
    const [ creating, setCreating ] = useState( false );
    const [ form, setForm ] = useState( BLANK_FORM );
    // Create waits for the Rulesys list; see EsRulesysSelect's onLoaded.
    const [ rulesysRead, setRulesysRead ] = useState( false );
    const onRulesysRead = useCallback( () => setRulesysRead( true ), [] );
    const [ saving, setSaving ] = useState( false );
    const [ saveError, setSaveError ] = useState( null );
    const [ joining, setJoining ] = useState( false );
    const [ code, setCode ] = useState( '' );
    const [ joinBusy, setJoinBusy ] = useState( false );
    const [ joinError, setJoinError ] = useState( null );
    const navigate = useNavigate();

    const readList = useCallback( ( pLive ) =>
        esFetch( API_CAMPAIGN_LIST )
            .then( ( pResponse ) => {
                if ( !pResponse.ok ) throw new Error( 'campaignList answered ' + pResponse.status );
                return pResponse.json();
            } )
            .then( ( pList ) => {
                if ( pLive && !pLive.current ) return;
                setCampaignList( pList );
                setStatus( STATUS_OK );
            } )
            .catch( ( pError ) => {
                console.error( 'Could not read your campaigns', pError );
                if ( !pLive || pLive.current ) setStatus( STATUS_ERROR );
            } ), [] );

    useEffect( () => {
        const aLive = { current: true };
        readList( aLive );
        return () => { aLive.current = false; };
    }, [ readList ] );

    // A functional update: the Rulesys dropdown hands back its default from an
    // effect, and a closure over the render before it would drop what was typed.
    const change = useCallback( ( pEvent ) => {
        const { name, value } = pEvent.target;
        setForm( ( pForm ) => ( { ...pForm, [ name ]: value } ) );
    }, [] );

    const create = () => {
        setSaving( true );
        setSaveError( null );

        // owner is not sent and could not be: the server takes the GM from the
        // session and writes the member row in the same transaction.
        // Straight into the new campaign, as join does (T130): the answer is its
        // EsCampaignDetail, so the slug is already in hand.
        esPost( API_CAMPAIGN, form )
            .then( ( pDetail ) => navigate( VIEW_CAMPAIGN + '/' + pDetail.key ) )
            .catch( ( pError ) => {
                console.error( 'Could not create the campaign', pError );
                setSaveError( esRefusalOf( pError, t( 'campaign-list-a-new-campaign', 'A new campaign' ) ) );
                setSaving( false );
            } );
    };

    // The code goes up exactly as typed. Trimming and upper-casing are the
    // server's, deliberately -- a phone that autocorrected the code to
    // lowercase still joins -- and doing either here as well would mean two
    // places deciding what a code is.
    //
    // On 200 the body is the whole EsCampaignDetail for the campaign just
    // joined, so the slug to go and look at it is already in hand and no second
    // read is needed. Straight there rather than back to a list the player has
    // never seen: they joined one campaign, and that is the one they want.
    const join = () => {
        setJoinBusy( true );
        setJoinError( null );

        esPost( API_CAMPAIGN_JOIN, { code } )
            .then( ( pDetail ) => navigate( VIEW_CAMPAIGN + '/' + pDetail.key ) )
            .catch( ( pError ) => {
                // "No such campaign" for a wrong code, a blank one, a closed
                // campaign and a deleted one alike. Shown as sent.
                console.error( 'Could not join with that code', pError );
                setJoinError( pError.message );
                setJoinBusy( false );
            } );
    };

    const joinForm = (
        <div className="campaignForm" data-testid="es-campaign-join-form" ref={ esFocusBlankOnMount }>
            <div className="campaignField">
                <label className="campaignLabel" htmlFor="code">
                    { t( 'campaign-list-join-code', 'Join code' ) }
                </label>
                <InputText id="code" name="code" type="text" fluid autoComplete="off"
                           data-testid="es-campaign-join-code-input"
                           // es-copy: literal
                           placeholder="K7QX2MTD"
                           value={ code }
                           onChange={ ( pEvent ) => setCode( pEvent.target.value ) } />
            </div>

            { joinError &&
                <p className="text-danger" data-testid="es-campaign-join-error">{ joinError }</p> }

            <div className="campaignFormButtons">
                <Button data-testid="es-campaign-join-button"
                        disabled={ joinBusy || !code.trim() } onClick={ join }>
                    { joinBusy ? t( 'campaign-list-joining', 'Joining…' ) : t( 'campaign-list-join', 'Join' ) }
                </Button>
                <Button data-testid="es-campaign-join-cancel" disabled={ joinBusy }
                        onClick={ () => { setJoining( false ); setJoinError( null ); } }>
                    { t( 'campaign-list-join-cancel', 'Cancel' ) }
                </Button>
            </div>
        </div>
    );

    const createForm = (
        <div className="campaignForm" data-testid="es-campaign-create-form" ref={ esFocusBlankOnMount }>
            <div className="campaignField">
                <label className="campaignLabel" htmlFor="name">
                    { t( 'campaign-list-name', 'Name' ) }
                </label>
                <InputText id="name" name="name" type="text" fluid autoComplete="off"
                           data-testid="es-campaign-create-name"
                           value={ form.name } onChange={ change } />
            </div>
            <div className="campaignField">
                <label className="campaignLabel" htmlFor="rulesysId">
                    { t( 'campaign-list-rulesystem', 'Rulesystem' ) }
                </label>
                <EsRulesysSelect id="rulesysId" fresh data-testid="es-campaign-create-rulesys"
                                 value={ form.rulesysId } onChange={ change } onLoaded={ onRulesysRead } />
            </div>
            <div className="campaignField">
                <label className="campaignLabel" htmlFor="blurb">
                    { t( 'campaign-list-blurb', 'Blurb' ) }
                </label>
                <EsTextarea id="blurb" name="blurb" rows={ 3 }
                            data-testid="es-campaign-create-blurb"
                            placeholder={ t( 'campaign-list-blurb-placeholder', 'What the table is about, in a line or two.' ) }
                            value={ form.blurb } onChange={ change } />
            </div>

            { saveError &&
                <p className="text-danger" data-testid="es-campaign-create-error">{ saveError }</p> }

            <div className="campaignFormButtons">
                <Button data-testid="es-campaign-create-button"
                        disabled={ saving || !form.name.trim() || !rulesysRead } onClick={ create }>
                    { saving ? t( 'campaign-list-creating', 'Creating…' ) : t( 'campaign-list-create', 'Create' ) }
                </Button>
                <Button data-testid="es-campaign-create-cancel" disabled={ saving }
                        onClick={ () => { setCreating( false ); setSaveError( null ); } }>
                    { t( 'campaign-list-create-cancel', 'Cancel' ) }
                </Button>
            </div>
        </div>
    );

    return (
        <section className="relative z-10 pb-4 pt-nav-room">
            { /* The minis while the empty state shows, as on the other sparse pages: not over a list or an open form. */ }
            { status === STATUS_OK && campaignList.length === 0 && !creating && !joining && <EsMinis /> }
            <div className="container">
                <div className="flex flex-wrap items-center justify-between mb-8 max-sm:gap-y-4">
                    <h1 className="text-2xl font-bold text-heading sm:text-3xl"
                        data-testid="es-campaign-list-heading">
                        { t( 'campaign-list-heading', 'Campaigns' ) }
                    </h1>
                    { /* Two ways in, side by side, and only one form open at a
                         time: opening either closes the other, because a page
                         offering a Create and a Join at once is a page where
                         the Enter key is ambiguous. */ }
                    <div className="campaignFormButtons">
                        { /* The shared demo table, in nobody's list (switchEsDemoMode). */ }
                        { demoOn &&
                            <Button data-testid="es-campaign-demo-open"
                                    onClick={ () => navigate( VIEW_DEMO ) }>
                                { t( 'campaign-list-demo', 'Try the Demo' ) }
                            </Button> }
                        { !joining &&
                            <Button data-testid="es-campaign-join-open" onClick={ () => {
                                setJoining( true );
                                setCreating( false );
                                setJoinError( null );
                            } }>
                                { t( 'campaign-list-join-open', 'Join with a Code' ) }
                            </Button> }
                        { !creating && maker &&
                            <Button data-testid="es-campaign-create-open" onClick={ () => {
                                setCreating( true );
                                setJoining( false );
                                setSaveError( null );
                                setRulesysRead( false );
                            } }>
                                { t( 'campaign-list-new', 'New Campaign' ) }
                            </Button> }
                    </div>
                </div>

                { joining && joinForm }

                { creating && createForm }

                { status === STATUS_LOADING &&
                    <EsLoading className="campaignsNote" data-testid="es-campaign-list-loading">
                        { t( 'campaign-list-reading', 'Reading your campaigns…' ) }
                    </EsLoading> }

                { status === STATUS_ERROR &&
                    <p className="text-danger" data-testid="es-campaign-list-error">
                        { t( 'campaign-list-unreadable',
                            'Your campaigns could not be read just now. Reload to try again.' ) }
                    </p> }

                { status === STATUS_OK && campaignList.length === 0 && !creating && !joining &&
                    <EsEmptyState size="screen" icon="pi-flag" data-testid="es-campaign-list-empty"
                                  textTestId="es-campaign-empty-message">
                        { /* The sentence and nothing after it, as of 2026-09-18.
                             It used to go on: "Create one and you are its GM, or
                             join an existing one with a code someone gave you."
                             That was written when the empty state carried its own
                             pair of buttons and had to say what they did; the
                             duplicate buttons went on 2026-09-16 and the toolbar
                             above is now the only place either way in is offered.
                             A sentence describing two controls that are already on
                             screen, labelled, is the page explaining itself. */ }
                        { t( 'campaign-list-none', 'You are in no campaigns yet.' ) }
                    </EsEmptyState> }

                { status === STATUS_OK && campaignList.length > 0 &&
                    <div className="campaignsGrid" data-testid="es-campaign-list">
                        { campaignList.map( ( pCampaign ) => (
                            <div key={ pCampaign.id }
                                 className="campaignCardLink"
                                 data-testid={ 'es-campaign-card-link-' + pCampaign.key }
                                 data-role={ pCampaign.role }
                                 role="button"
                                 tabIndex={ 0 }
                                 onKeyDown={ ( pEvent ) => {
                                     if ( pEvent.key === 'Enter' || pEvent.key === ' ' ) {
                                         pEvent.preventDefault();
                                         void navigate( VIEW_CAMPAIGN + '/' + pCampaign.key );
                                     }
                                 } }
                                 onClick={ () =>
                                     navigate( VIEW_CAMPAIGN + '/' + pCampaign.key ) }>
                                { /* Keyed by slug, not by id: a feature file knows
                                     the name it created a campaign under and so
                                     knows the slug, and knows nothing about the
                                     row id the database minted. EsCard hangs
                                     `-title` off this one for the name; the
                                     subtitle is not hooked, so the summary line
                                     is read through the card. */ }
                                <EsCard
                                    className={ ( 'campaignCard ' + ( ROLE_CARD_CLASS[ pCampaign.role ] ?? '' ) ).trim() }
                                    data-testid={ 'es-campaign-card-' + pCampaign.key }
                                    title={ pCampaign.name || t( 'campaign-list-unnamed', 'Unnamed' ) }
                                    subTitle={ summarise( t, pCampaign ) }>
                                    <p className="campaignCardStat"
                                       data-testid={ 'es-campaign-card-stat-' + pCampaign.key }>
                                        { pCampaign.blurb || lastPlayed( t, pCampaign ) }
                                    </p>
                                </EsCard>
                            </div>
                        ) ) }
                    </div> }
            </div>
        </section>
    );
}
